import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import UnitsPage from "./pages/UnitsPage";
import TenantsPage from "./pages/TenantsPage";
import ContractsPage from "./pages/ContractsPage";
import ReservationsPage from "./pages/ReservationsPage";
import AccrualsPage from "./pages/AccrualsPage";
import PaymentsPage from "./pages/PaymentsPage";
import ReportsPage from "./pages/ReportsPage";
import MaintenancePage from "./pages/MaintenancePage";
import BankAccountsPage from "./pages/BankAccountsPage";
import PropertiesPage from "./pages/PropertiesPage";
import TenantPortal from "./pages/TenantPortal";
import DebtTrackingPage from "./pages/DebtTrackingPage";
import ProfilePage from "./pages/ProfilePage";
import UserManagementPage from "./pages/UserManagementPage";
import DuesTrackingPage from "./pages/DuesTrackingPage";

// Layout
import AdminLayout from "./components/layout/AdminLayout";
import TenantLayout from "./components/layout/TenantLayout";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-black border-t-transparent animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    // Redirect tenants to their portal
    if (user?.role === 'tenant') {
      return <Navigate to="/tenant-portal" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router with auth callback detection
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Admin Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'finance', 'operations', 'maintenance']}>
            <AdminLayout>
              <Dashboard />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/properties"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
            <AdminLayout>
              <PropertiesPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/units"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'operations']}>
            <AdminLayout>
              <UnitsPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenants"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'operations']}>
            <AdminLayout>
              <TenantsPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contracts"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'operations']}>
            <AdminLayout>
              <ContractsPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reservations"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'operations']}>
            <AdminLayout>
              <ReservationsPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/accruals"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'finance']}>
            <AdminLayout>
              <AccrualsPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'finance']}>
            <AdminLayout>
              <PaymentsPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'finance']}>
            <AdminLayout>
              <ReportsPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/maintenance"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'operations', 'maintenance']}>
            <AdminLayout>
              <MaintenancePage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bank-accounts"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
            <AdminLayout>
              <BankAccountsPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/debt-tracking"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'finance']}>
            <AdminLayout>
              <DebtTrackingPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      {/* Tenant Portal */}
      <Route
        path="/tenant-portal"
        element={
          <ProtectedRoute allowedRoles={['tenant']}>
            <TenantLayout>
              <TenantPortal />
            </TenantLayout>
          </ProtectedRoute>
        }
      />

      {/* Profile */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'finance', 'operations', 'maintenance', 'staff']}>
            <AdminLayout>
              <ProfilePage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      {/* User Management */}
      <Route
        path="/user-management"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
            <AdminLayout>
              <UserManagementPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      {/* Dues Tracking */}
      <Route
        path="/dues-tracking"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'finance']}>
            <AdminLayout>
              <DuesTrackingPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <AppRouter />
          <Toaster position="top-right" />
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
